import React, { useEffect, useMemo } from 'react'
import { Box } from 'rebass'
import _ from 'lodash'
import { useNewTranslation } from 'hooks/index'
import i18n from 'config/i18n'

import { useDispatch, useSelector } from 'react-redux'
import { Redirect } from 'react-router-dom'
import qs from 'query-string'

import { selectCampaign, selectCampaignProgress, selectRequestsAreLoading } from 'state'
import {
    getCampaignProgress,
    clearCampaignProgress,
    getCampaign,
    exportCampaignProgress,
    CampaignProgressState,
} from 'state/campaigns'
import { getTasksList, clearTasksList, selectTasks } from 'state/tasks'
import { STATE_KEYS } from 'state/requests'
import { MAX_PAGE_SIZE, selectActivePageNum, selectSearchTerm } from 'state/review'
import { theme } from 'theme'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import PartiTable from 'components/Shared/MuiTable/PartiTable'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import { PageHeader } from '.'
import ExportModal from './Review/common/ExportModal'
import { getStepDataHeaderCell } from 'components/Shared/MuiTable/Cell/StepDataHeaderCell'
import { requestProfileOptions } from 'state/constants'
import { selectPartiposterProfiles } from 'state/partiposter'
import { TableViewType } from 'components/Shared/MuiTable'
import { PLATFORM_ENUM } from 'services/api/constants'
import { setTeamCountryByCampaign } from 'state/filter'
import { getCampaignFollowerRequirements, showDefaultIgPlatform } from 'components/utils/campaign'
import { ProfileCell } from 'components/Shared/MuiTable/Cell/ProfileCell'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'
import { SocialMediaInfoCell } from 'components/Shared/MuiTable/Cell/SocialMediaInfoCell'

const SUBMISSION_STATUS: any = {
    0: {
        value: i18n.t('statusLabel.started'),
        color: 'warning',
    },
    1: {
        value: i18n.t('statusLabel.inProgress'),
        color: 'warning',
    },
    2: {
        value: i18n.t('statusLabel.rejected'),
        color: 'error',
    },
    3: {
        value: i18n.t('statusLabel.finished'),
        color: 'positive',
    },
    4: {
        value: i18n.t('statusLabel.paid'),
        color: 'positive',
    },
    99: {
        value: i18n.t('statusLabel.closed'),
        color: 'disable',
    },
}

const STEP_STATUS: any = {
    0: {
        value: i18n.t('statusLabel.notDone'),
        color: 'warning',
    },
    1: {
        value: i18n.t('statusLabel.pending'),
        color: 'warning',
    },
    2: {
        value: i18n.t('statusLabel.approved'),
        color: 'positive',
    },
    3: {
        value: i18n.t('statusLabel.actionRequired'),
        color: 'error',
    },
    4: {
        value: i18n.t('statusLabel.editsRequested'),
        color: 'error',
    },
    99: {
        value: i18n.t('statusLabel.completed'),
        color: 'positive',
    },
}

interface ProgressProps {
    location: any
}

const Progress: React.FC<ProgressProps> = ({ location }) => {
    const [t] = useNewTranslation()
    const dispatch = useDispatch()
    const params = qs.parse(location.search)

    const { campaign: campaignUuid } = params

    const campaign = useSelector(selectCampaign(campaignUuid as string))
    const campaignProgress = useSelector(selectCampaignProgress)
    const profiles = useSelector(selectPartiposterProfiles)
    const tasks = useSelector(selectTasks)
    const campaignPlatforms = useMemo(() => {
        return _.uniq(tasks.map(task => task.platform))
    }, [tasks])

    const isLoading = useSelector(
        selectRequestsAreLoading([STATE_KEYS.Campaign, STATE_KEYS.TasksList, STATE_KEYS.CampaignProgress]),
    )
    const isExportRequesting = useSelector(selectRequestsAreLoading([STATE_KEYS.ExportProgress]))
    const activePageNum = useSelector(selectActivePageNum)
    const searchTerm = useSelector(selectSearchTerm)

    const handleClickExport = () => {
        if (campaign && campaign.uuid) dispatch(exportCampaignProgress({ uuid: campaign.uuid }))
    }

    useEffect(() => {
        const offset: number = activePageNum * MAX_PAGE_SIZE
        if (campaignUuid && !isLoading) {
            dispatch(
                getCampaign({
                    uuid: campaignUuid as string,
                    onSuccess: campaign => {
                        dispatch(setTeamCountryByCampaign(campaign.uuid))
                    },
                }),
            )
            dispatch(getCampaignProgress({ uuid: campaignUuid as string, offset, keywords: searchTerm }))
            dispatch(getTasksList({ campaignUuid: campaignUuid as string }))
        }
        return () => {
            dispatch(clearTasksList())
            dispatch(clearCampaignProgress())
        }
    }, [campaignUuid, activePageNum, searchTerm])

    useEffect(() => {
        if (campaign) dispatch(requestProfileOptions({ country: campaign.country }))
    }, [campaign])

    const followerRequirementsPlatform = useMemo(() => getCampaignFollowerRequirements(campaign), [campaign])

    const hasPlatform = (platform: PLATFORM_ENUM) => {
        return campaignPlatforms.includes(platform) || followerRequirementsPlatform.includes(platform)
    }

    const data = useMemo(
        () =>
            campaignProgress.map((progress: CampaignProgressState) => {
                const { submission_tasks: tasks, status: submissionStatus } = progress

                const steps = tasks.map((task: any) => {
                    return task.progress.map((step: any) => {
                        return {
                            uuid: step.step_uuid,
                            taskUuid: task.task_uuid,
                            status: step.status === 1 ? 99 : task.status,
                        }
                    })
                })

                return {
                    partiposterProfile: profiles[progress.partiposterId],
                    steps: _.keyBy(_.flatten(steps), 'uuid'),
                    submissionStatus,
                }
            }),
        [campaignProgress, profiles],
    )

    const showIgPlatform = useMemo(
        () => showDefaultIgPlatform(campaignPlatforms, followerRequirementsPlatform),
        [campaignPlatforms, followerRequirementsPlatform],
    )

    const extraColumns = useMemo(() => {
        const HEADER_NAME_SPLITTER = ' - '
        return tasks
            .map((task: any, index: number) => {
                const statusArr = task.steps.map((step: any) => {
                    return {
                        field: task.uuid + '_' + step.uuid,
                        headerName: `${task.title}${HEADER_NAME_SPLITTER}${step.data.title}`,
                        width: 210,
                        renderHeader: getStepDataHeaderCell(HEADER_NAME_SPLITTER),
                        renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                        sortable: false,
                        filterable: false,
                    }
                })
                return statusArr
            })
            .flat()
    }, [tasks])

    const columns = useMemo(
        () => [
            {
                field: 'profile',
                type: 'string',
                headerName: t('submission.table.profile'),
                width: 160,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
                subFields: [
                    {
                        field: 'name',
                        headerName: t('submission.table.nameLabel'),
                    },
                    {
                        field: 'gender',
                        headerName: t('submission.table.genderLabel'),
                    },
                    {
                        field: 'age',
                        headerName: t('submission.table.ageLabel'),
                    },
                ],
            },
            {
                field: 'socialMediaInfo',
                type: 'string',
                headerName: t('submission.table.socialMediaInfo'),
                width: 180,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <SocialMediaInfoCell {...props} />,
                subFields: [
                    ...(showIgPlatform
                        ? [
                              {
                                  field: FIELDS_TYPE.IG_FOLLOWERS,
                                  headerName: t('submission.table.instagramFollowersLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.IG_USERNAME,
                                  headerName: t('submission.table.instagramUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(PLATFORM_ENUM.FB)
                        ? [
                              {
                                  field: FIELDS_TYPE.FB_FRIENDS,
                                  headerName: t('submission.table.facebookFriendsLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.FB_USERNAME,
                                  headerName: t('submission.table.facebookUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(PLATFORM_ENUM.TIKTOK)
                        ? [
                              {
                                  field: FIELDS_TYPE.TIKTOK_FOLLOWERS,
                                  headerName: t('submission.table.tikTokFollowersLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.TIKTOK_USERNAME,
                                  headerName: t('submission.table.tikTokUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(PLATFORM_ENUM.TWITTER)
                        ? [
                              {
                                  field: FIELDS_TYPE.TWITTER_FOLLOWERS,
                                  headerName: t('submission.table.twitterFollowersLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.TWITTER_USERNAME,
                                  headerName: t('submission.table.twitterUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(PLATFORM_ENUM.XIAOHONGSHU)
                        ? [
                              {
                                  field: FIELDS_TYPE.XIAOHONGSHU_FOLLOWERS,
                                  headerName: t('submission.table.XHSFollowersLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.XIAOHONGSHU_USERNAME,
                                  headerName: t('submission.table.XHSUsernameLabel'),
                              },
                          ]
                        : []),
                ],
            },
            ...extraColumns,
            {
                field: 'status',
                headerName: t('submission.table.statusLabel'),
                width: 160,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                sortable: false,
                filterable: false,
            },
        ],
        [extraColumns, campaignPlatforms, followerRequirementsPlatform],
    )

    interface Row {
        [key: string]: { value: string; color?: string }
    }

    const getExtraRows = (progress: any) => {
        const perfArr = Object.keys(progress.steps).map((stepUuid: string) => {
            const stepData = progress.steps[stepUuid]
            const rowsArr = []
            const rowObj: Row = {}
            const rowKey = stepData.taskUuid + '_' + stepData.uuid
            rowObj[rowKey] = Object.keys(STEP_STATUS).includes(stepData.status.toString())
                ? STEP_STATUS[stepData.status]
                : {
                      value: t('statusLabel.notDone'),
                      color: 'disable',
                  }
            rowsArr.push(rowObj)
            const taskTypeRow: Row = {}
            const task = tasks.find((task: any) => task.uuid === stepData.taskUuid)
            taskTypeRow[stepData.taskUuid + '_taskName'] = { value: task ? task.title : '' }
            rowsArr.push(taskTypeRow)
            return rowsArr.flat()
        })
        return perfArr.flat()
    }

    const rows = useMemo(
        () =>
            data.map(progress => {
                const { partiposterProfile } = progress
                const { social_media_accounts: socialMediaAccounts } = partiposterProfile
                const igProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.IG)
                const fbProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.FB)
                const tiktokProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.TIKTOK)
                const twitterProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.TWITTER)
                const xiaohongshuProfile = socialMediaAccounts.find(
                    account => account.platform === PLATFORM_ENUM.XIAOHONGSHU,
                )
                const extraRows = getExtraRows(progress)
                return Object.assign(
                    {
                        id: partiposterProfile.uuid,
                        profile: { profile: partiposterProfile },
                        socialMediaInfo: {
                            profiles: [igProfile, fbProfile, tiktokProfile, twitterProfile, xiaohongshuProfile],
                        },
                        status: SUBMISSION_STATUS[progress.submissionStatus as number],
                    },
                    ...extraRows,
                )
            }),
        [campaignUuid, data, campaign],
    )

    const enableExport = rows.length > 0

    if (!campaignUuid) return <Redirect to="/submissions" />

    return (
        <>
            <Box mx={theme.spacing.xs} mt={theme.spacing.xs}>
                {campaign ? <PageHeader title={campaign.title} subTitle={t('button.partiposterProgress')} /> : null}
                <PartiTable
                    rowHeight={84}
                    columns={columns}
                    rows={rows}
                    isLoading={isLoading}
                    viewType={TableViewType.PROGRESS}
                    hideToolbar={false}
                    enableExport={enableExport}
                />
            </Box>
            <ExportModal
                tableTitle={t('submission.exportList.partipostProgressTableTitle')}
                isRequesting={isExportRequesting}
                onClickExport={handleClickExport}
            />
        </>
    )
}

export default Progress
